import { createContext, useContext, useEffect, useState } from 'react';
import { getDatalayerObject } from '../dataLayer/datalayerUtil';
import { useUserSessionStore, UserSessionState, UserSessionActions } from './user-session-store';
import { logger } from '../logger';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { GlobalPageProps, SessionData } from './types';
import { isEmpty } from '@marriott/shared/mi-helper-utils';
import { fetchConsumerData } from '../consumerUxlApis/consumerUxlApisUtil';
import { IConsumerDataResponse } from '../consumerUxlApis/types/consumerUXlResponse.types';
import { RENDERER_TYPE } from '../constants';
import { getIsACDLenabledFlag } from '../helpers';

declare global {
  interface Window {
    adobeDataLayer: {
      [key: number]: {
        event: string;
        [key: string]: { [key: string]: unknown } | string | undefined | boolean;
      };
      version: number;
      push: (event: {
        event: string;
        [key: string]: { [key: string]: unknown } | string | undefined | boolean;
      }) => void;
      getState: () => Record<string, unknown>;
      addEventListener: (type: string, listener: EventListenerOrEventListenerObject) => void;
      removeEventListener: (type: string, listener: EventListenerOrEventListenerObject) => void;
    };
  }
}

type UserSessionProvider = {
  pageProps: GlobalPageProps; // @fixme: this is way too much stuff to pass around
  includePiData?: boolean; // flag to check if PI data needs to be included in datalayer or not
  /* Children to render */
  children: React.ReactNode;
  hasOwnDataLayer?: boolean; // this flag will set to true if product team has their own server/client datalayer call
  renderer?: RENDERER_TYPE;
  webSdkV2?: boolean;
};

export type DataLayerEvent = {
  event: string;
  skipDatalayerUpdate?: boolean;
  [key: string]: { [key: string]: unknown } | string | undefined | boolean;
};

export type MvpOffersData = {
  specialMessage?: string;
  offerCode?: string;
  eventInfo?: string;
  placementGroup?: string;
  rpcCode?: string;
  offerIdentifier?: string;
  source?: string;
  isFocComplete?: string;
};

export type UserSessionContextType = {
  session: SessionData;
  mvpOffers?: MvpOffersData;
  datalayer?: Record<string, unknown>;
  updateSession: (sessionData: SessionData) => void;
  updateDatalayer: (eventData: DataLayerEvent) => void;
  getDatalayer: () => Record<string, unknown> | null;
  syncedWithServer: boolean;
  isACDLenabled: boolean;
  /**
   * Raw response from the already-fetched ContactInformation (cobrandDatalayer) query.
   * Exposed here so consumers (e.g. DataTiles) can read loyalty/rewards data without
   * firing a second network call. `undefined` until the provider query resolves.
   */
  consumerData?: IConsumerDataResponse;
};

const UserSessionContext = createContext<UserSessionContextType>({
  session: {},
  updateSession: () => {},
  updateDatalayer: () => {},
  getDatalayer: () => null,
  syncedWithServer: false,
  isACDLenabled: false,
});

UserSessionContext.displayName = 'UserSessionContext';

/**
 * Provider to manage user session data and sync with server on initial load and updates.
 */
export function UserSessionProvider({
  pageProps,
  includePiData = false,
  hasOwnDataLayer = false,
  children,
  renderer,
  webSdkV2 = false,
}: React.PropsWithChildren<UserSessionProvider>) {
  const { log } = logger({})('UserSessionProvider');
  const [datalayer, setDatalayer] = useState<Record<string, unknown>>();
  const mvpOffers = useUserSessionStore(state => state.mvpOffers);
  const updateMvpOffersData = useUserSessionStore(state => state.updateMvpOffersData);
  const session = useUserSessionStore(state => state.session);
  const updateSessionStore = useUserSessionStore(state => state.updateSession);
  const { syncedWithServer, isPrequalComplete } = useUserSessionStore(
    (state: UserSessionState & UserSessionActions) => state
  );

  const envVarsObject = useClientEnvVarsStore(state => state.envVarsObject);
  const isEnableWebSDK = getIsACDLenabledFlag(pageProps);

  const [customerDataAPIdetails, setCustomerDataAPIdetails] = useState<{
    data: IConsumerDataResponse | undefined;
    isLoaded: boolean;
  }>({
    data: undefined,
    isLoaded: false,
  });

  const updateDatalayer = (payload: DataLayerEvent) => {
    // if skipDatalayerUpdate is passed as false, it will update the datalayer state and all components which uses this state will renderer. by default we are making this as true which will not update the state
    const skipDataLayerFlag = payload?.skipDatalayerUpdate ?? true;
    if (!skipDataLayerFlag) {
      setDatalayer(state => ({ ...state, ...(payload?.['data'] as { [key: string]: unknown }) }));
    }
    if (payload?.skipDatalayerUpdate) {
      delete payload?.skipDatalayerUpdate;
    }
    if (isEnableWebSDK) {
      log.info('pushing to adobeDataLayer', payload);
      window.adobeDataLayer?.push(payload);
      log.info('updating the session datalayer', payload);
    } else {
      window.dataLayer = {
        ...datalayer,
        ...(payload?.['data'] as { [key: string]: unknown }),
      };
    }
  };

  const getDatalayer = () => {
    return window.adobeDataLayer?.getState();
  };

  const updateSession = (sessionData: SessionData) => {
    updateSessionStore(sessionData, true); // during update session, data in session store should be merged. So passing second attribute as true
  };

  /**
   * Load datalayer data from window object if available.
   */
  useEffect(() => {
    // @todo: for backward compatibility support, should be removed once all apps are using web sdk
    if (isEmpty(envVarsObject)) {
      return;
    }
    /**
     * this state update should happen only once, thats why added additional condition i.e., if datalayer is empty
     * and this has to be trigger only when acdl is off
     */

    if (
      !isEmpty(window.dataLayer) &&
      !datalayer &&
      (String(envVarsObject['NEXT_PUBLIC_ENABLE_WEBSDK']) !== 'true' ||
        String(pageProps?.model?.enableWebSDK) !== 'true')
    ) {
      log.info('loading datalayer data from window', window.dataLayer);
      setDatalayer(window.dataLayer);
    }
  }, [JSON.stringify(envVarsObject), datalayer]);

  /**
   * Load MVP offers data from window object for non acdl products if available.
   */
  useEffect(() => {
    if (isEmpty(envVarsObject)) {
      return;
    }
    // @todo: for backward compatibility support, should be removed once all apps have enabled prequal call
    if (
      (!envVarsObject['NEXT_PUBLIC_ENABLE_WEBSDK'] || String(pageProps?.model?.enableWebSDK) !== 'true') &&
      !isEmpty(window.mvpOffers)
    ) {
      log.info('web sdk is not enabled');
      log.info('loading mvp offers data from window', window.mvpOffers);
      updateMvpOffersData(window.mvpOffers);
      return;
    }
  }, [JSON.stringify(envVarsObject)]);

  /**
   * Initialize the dataLayer with session data and mvpOffers data.
   */
  useEffect(() => {
    const initialize = async () => {
      try {
        if (
          window?.location?.search?.indexOf('enableCobrandAcdl') > -1 ||
          String(envVarsObject?.['ENABLE_ACDL']) === 'true'
        ) {
          const consumerDataResp = (await fetchConsumerData(
            session,
            pageProps?.resolvedUrl,
            renderer as RENDERER_TYPE,
            webSdkV2,
            pageProps?.model
          )) as IConsumerDataResponse;
          log.info('consumer data fetched!');
          setCustomerDataAPIdetails(prev => ({ ...prev, data: consumerDataResp }));
        }
        log.info('UserSessionProvider init!');
      } catch (error) {
        log.error('Error updating dataLayer', error);
      } finally {
        setCustomerDataAPIdetails(prev => ({ ...prev, isLoaded: true }));
      }
    };

    if (isEnableWebSDK && syncedWithServer) {
      initialize();
    }
  }, [syncedWithServer]);

  useEffect(() => {
    if (syncedWithServer && isPrequalComplete && customerDataAPIdetails.isLoaded) {
      updateDatalayer({
        event: 'dataLayerLoaded',
        data: getDatalayerObject(
          session,
          pageProps,
          includePiData,
          customerDataAPIdetails.data,
          renderer as RENDERER_TYPE,
          hasOwnDataLayer
        ),
        skipDatalayerUpdate: false,
      });
    }
  }, [syncedWithServer, isPrequalComplete, customerDataAPIdetails.isLoaded]);

  const contextValue = {
    session: (!isEnableWebSDK && typeof syncedWithServer === 'boolean') || syncedWithServer ? session : {},
    datalayer,
    mvpOffers,
    updateSession,
    updateDatalayer,
    getDatalayer,
    syncedWithServer,
    isACDLenabled: isEnableWebSDK,
    consumerData: customerDataAPIdetails.data,
  };
  return <UserSessionContext.Provider value={contextValue}>{children}</UserSessionContext.Provider>;
}

export function useSession() {
  const context = useContext(UserSessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a UserSessionProvider');
  }
  return context;
}
